import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Bell, Check, CheckCheck, Trash2, AlertTriangle, Calendar, Mail, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const NotificationsPage = () => {
  const { token, isAdmin } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailStatus, setEmailStatus] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      toast.error('خطأ في تحميل الإشعارات');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailStatus = async () => {
    try {
      const response = await axios.get(`${API}/email/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailStatus(response.data);
    } catch (error) {
      console.log('Could not fetch email status');
    }
  };

  const sendEmailNotifications = async () => {
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/notifications/email/send-expiring`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'خطأ في إرسال البريد الإلكتروني';
      toast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchEmailStatus();
  }, [token]);

  const markAsRead = async (notificationId) => {
    try {
      // Mark as read first
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Then delete the notification automatically
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local state
      setNotifications(notifications.filter(n => n.id !== notificationId));
      toast.success('تم حذف الإشعار');
    } catch (error) {
      toast.error('خطأ في حذف الإشعار');
    }
  };

  const markAllAsRead = async () => {
    try {
      // Mark all as read
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Delete all read notifications one by one
      const deletePromises = notifications.map(n => 
        axios.delete(`${API}/notifications/${n.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      await Promise.all(deletePromises);
      setNotifications([]);
      toast.success('تم حذف جميع الإشعارات');
    } catch (error) {
      toast.error('خطأ في حذف الإشعارات');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      cars: '🚗',
      workers: '👥',
      rentals: '🏠',
      real_estate: '🏢',
    };
    return icons[category] || '📋';
  };

  const getCategoryColor = (category) => {
    const colors = {
      cars: 'bg-blue-100 text-blue-700',
      workers: 'bg-purple-100 text-purple-700',
      rentals: 'bg-rose-100 text-rose-700',
      real_estate: 'bg-emerald-100 text-emerald-700',
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  const getDaysRemaining = (dateStr) => {
    const today = new Date();
    const expiry = new Date(dateStr);
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getUrgencyBadge = (dateStr) => {
    const days = getDaysRemaining(dateStr);
    if (days < 0) return <Badge variant="destructive">منتهي</Badge>;
    if (days <= 7) return <Badge variant="destructive">عاجل</Badge>;
    if (days <= 14) return <Badge className="bg-amber-500">قريب</Badge>;
    return <Badge variant="secondary">تنبيه</Badge>;
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 flex-row-reverse">
        <div className="text-right">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">الإشعارات</h1>
          <p className="text-slate-500 mt-1">
            {unreadCount > 0 ? `لديك ${unreadCount} إشعار غير مقروء` : 'لا توجد إشعارات جديدة'}
          </p>
        </div>
        <div className="flex gap-2 flex-row-reverse">
          {isAdmin && (
            <Button
              onClick={sendEmailNotifications}
              disabled={sendingEmail || !emailStatus?.configured}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="send-email-btn"
              title={!emailStatus?.configured ? 'خدمة البريد غير مهيأة' : 'إرسال تنبيه بالبريد'}
            >
              {sendingEmail ? (
                <Loader2 className="w-4 h-4 ml-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 ml-2" />
              )}
              إرسال تنبيه بالبريد
            </Button>
          )}
          {unreadCount > 0 && (
            <Button
              onClick={markAllAsRead}
              variant="outline"
              data-testid="mark-all-read-btn"
            >
              <Trash2 className="w-4 h-4 ml-2" />
              حذف الكل
            </Button>
          )}
        </div>
      </div>

      {/* Email Status Banner */}
      {isAdmin && emailStatus && !emailStatus.configured && (
        <Card className="border-0 shadow-sm bg-amber-50 border-r-4 border-r-amber-500">
          <CardContent className="p-4 flex items-center gap-3 justify-start">
            <Mail className="w-5 h-5 text-amber-600" />
            <div className="text-right">
              <p className="text-amber-800 font-medium">خدمة البريد الإلكتروني غير مهيأة</p>
              <p className="text-amber-600 text-sm">يرجى إضافة RESEND_API_KEY و NOTIFICATION_EMAIL في إعدادات النظام</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Configured Banner */}
      {isAdmin && emailStatus && emailStatus.configured && (
        <Card className="border-0 shadow-sm bg-emerald-50 border-r-4 border-r-emerald-500">
          <CardContent className="p-4 flex items-center gap-3 justify-start">
            <Mail className="w-5 h-5 text-emerald-600" />
            <div className="text-right flex-1">
              <p className="text-emerald-800 font-medium">✓ خدمة البريد الإلكتروني مفعّلة</p>
              <p className="text-emerald-600 text-sm">
                يتم إرسال تنبيه دوري كل 15 يوم الساعة {emailStatus.scheduled_time || '08:00 UTC'}
                {emailStatus.notification_email && ` إلى ${emailStatus.notification_email}`}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notifications List */}
      <Card className="border-0 shadow-sm" data-testid="notifications-card">
        <CardContent className="p-0">
          {notifications.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Bell className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">لا توجد إشعارات</p>
              <p className="text-sm mt-2">ستظهر هنا التنبيهات الخاصة بانتهاء المواعيد</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[600px]">
              <div className="divide-y divide-slate-100">
                {notifications.map((notification, index) => (
                  <div
                    key={notification.id}
                    className={`p-4 lg:p-6 transition-colors ${
                      notification.is_read ? 'bg-white' : 'bg-amber-50/50'
                    } hover:bg-slate-50`}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex items-start gap-4 flex-row-reverse">
                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${getCategoryColor(notification.category)}`}>
                        {getCategoryIcon(notification.category)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 text-right">
                        <div className="flex items-start justify-between gap-2 flex-row-reverse">
                          <div>
                            <h3 className="font-semibold text-slate-900 flex items-center gap-2 justify-end">
                              {!notification.is_read && (
                                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                              )}
                              {notification.title}
                            </h3>
                            <p className="text-slate-600 mt-1">{notification.message}</p>
                          </div>
                          {getUrgencyBadge(notification.expiry_date)}
                        </div>

                        {/* Meta info */}
                        <div className="flex items-center gap-4 mt-3 text-sm text-slate-500 justify-end">
                          <span>
                            باقي: <span className="number font-medium">
                              {getDaysRemaining(notification.expiry_date) > 0 
                                ? `${getDaysRemaining(notification.expiry_date)} يوم`
                                : 'منتهي'
                              }
                            </span>
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="number">{notification.expiry_date}</span>
                            :تاريخ الانتهاء
                            <Calendar className="w-4 h-4" />
                          </span>
                        </div>

                        {/* Actions */}
                        {!notification.is_read && (
                          <div className="mt-3 flex justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => markAsRead(notification.id)}
                              data-testid={`mark-read-${notification.id}`}
                            >
                              <Trash2 className="w-4 h-4 ml-1" />
                              حذف
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Summary Card */}
      {notifications.length > 0 && (
        <Card className="border-0 shadow-sm bg-slate-900 text-white" data-testid="notifications-summary">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">ملخص التنبيهات</h3>
                <p className="text-white/70">
                  لديك {notifications.filter(n => getDaysRemaining(n.expiry_date) <= 7).length} تنبيه عاجل و {' '}
                  {notifications.filter(n => getDaysRemaining(n.expiry_date) > 7 && getDaysRemaining(n.expiry_date) <= 30).length} تنبيه قادم
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NotificationsPage;
